import { notification, Tag } from 'antd';
import { createUpdateOverrides, getOverrides, removeOverrides } from '../../services/adInvestigator';
import { USER_PROFILE, DEVICE, ADS, VIDEO_AD_TYPE, PAUSE_AD_TYPE, SWID, FUBO_USER, VIEWER_ID } from '../../constants';
import { getFromRuleKey } from '../../utils';
import { searchParamsSelector } from '../app';
import moment from 'moment-timezone';

export const extractAppFromRule = (key) => {
  const keyApp = getFromRuleKey(key || '', 'app');
  return typeof keyApp === 'string' && keyApp ? keyApp : '';
};

export const adInvestigator = {
  namespace: 'adInvestigator',
  state: {
    rules: [],
    userWidths: {},
    deviceWidths: {},
  },
  reducers: {
    updateExistingRules(state, { payload: rules }) {
      return { ...state, rules };
    },
    overrideUserWidths(state, { payload: userWidths }) {
      return { ...state, userWidths };
    },
    overrideDeviceWidths(state, { payload: deviceWidths }) {
      return { ...state, deviceWidths };
    },
  },
  effects: {
    *pageInit(emptyPayload, { put, select }) {
      yield put({ type: 'adCampaignProvider/fetchObjectData', payload: ADS });
      const searchParams = yield select(searchParamsSelector);
      yield put({
        type: 'fetchExistingRules',
        payload: { overrideBy: searchParams?.viewType ?? USER_PROFILE },
      });
    },
    *removeRule({ payload: { adType, userId, profileId, deviceId, swid, fuboUserId, viewerId, app } }, { call, put }) {
      let overrideBy = '';
      if (userId && profileId) {
        overrideBy = USER_PROFILE;
      } else if (deviceId) {
        overrideBy = DEVICE;
      } else if (fuboUserId) {
        overrideBy = FUBO_USER;
      } else if (swid) {
        overrideBy = SWID;
      } else if (viewerId) {
        overrideBy = VIEWER_ID;
      } else {
        yield call(notification.error, {
          message: 'No matching Rule found.',
          description: 'The rule may have already been removed.',
          duration: 3,
        });
        return;
      }

      (yield call(removeOverrides, {
        adType,
        overrideBy,
        userId,
        profileId,
        deviceId,
        swid,
        fuboUserId,
        viewerId,
        app,
      })) &&
        (yield put({ type: 'pageInit' })) &&
        (yield call(notification.success, {
          message: 'Success deleting Override Rule.',
          duration: 3,
        }));
    },
    *submitRule(
      {
        payload: {
          overrideBy,
          adType,
          userId,
          profileId,
          deviceId,
          swid,
          fuboUserId,
          viewerId,
          apps,
          overrideRules,
          pauseAdId,
          expire,
        },
      },
      { call, put }
    ) {
      if (adType === VIDEO_AD_TYPE && !overrideRules) {
        yield call(notification.error, {
          message: 'Need Pod & Slot for override rule.',
          duration: 3,
        });
        return;
      }

      const rules = {
        'expire-at': expire.unix(),
      };

      if (adType === VIDEO_AD_TYPE) {
        rules['pod-list'] = overrideRules.map(({ pod, adId, slot }) => {
          return {
            'pod-position': pod,
            'slot-list': [
              {
                'ad-id': adId,
                number: slot,
              },
            ],
          };
        });
      } else {
        rules['ad-id'] = pauseAdId;
      }

      if (Array.isArray(apps) && apps.length > 0) {
        rules['app-list'] = apps;
      }

      const overrides = {
        overrideBy,
        adType,
        userId,
        profileId,
        deviceId,
        swid,
        fuboUserId,
        viewerId,
        rules: rules,
      };

      (yield call(createUpdateOverrides, overrides)) &&
        (yield put({ type: 'pageInit' })) &&
        (yield call(notification.success, {
          message: 'Created/Updated Override Rule.',
          duration: 3,
        }));
    },
    *fetchExistingRules({ payload: overrideInfo }, { call, all, put }) {
      const videoAdsOverridesPromise = call(getOverrides, overrideInfo, VIDEO_AD_TYPE);
      const videoAdsOverridesResponse = yield videoAdsOverridesPromise;
      let pauseAdsOverridesResponse = null;
      if (![SWID, FUBO_USER, VIEWER_ID].includes(overrideInfo.overrideBy)) {
        const pauseAdsOverridesPromise = call(getOverrides, overrideInfo, PAUSE_AD_TYPE);
        pauseAdsOverridesResponse = yield pauseAdsOverridesPromise;
      }
      const combinedRules = [];
      if (videoAdsOverridesResponse?.data) {
        const response = videoAdsOverridesResponse;
        const fetchedRules = Array.isArray(response.data) ? response.data : [response.data];
        const rules = fetchedRules.map(
          ({ key, 'pod-list': positionsRaw, 'expire-at': expireAt, 'editor-email': editorEmail }, ruleIndex) => {
            const safeKey = key || '';
            const app = extractAppFromRule(safeKey);
            const mappedPositions = {};
            const positions = Array.isArray(positionsRaw) ? positionsRaw : [];
            positions.forEach(({ 'pod-position': pod, 'slot-list': slots }) => {
              const slotList = Array.isArray(slots) ? slots : [];
              mappedPositions[pod] = mappedPositions[pod] ? mappedPositions[pod].concat(slotList) : slotList;
            });
            return {
              key: `${key}-${ruleIndex}`,
              adType: VIDEO_AD_TYPE,
              userId: getFromRuleKey(safeKey, 'user'),
              profileId: getFromRuleKey(safeKey, 'profile'),
              deviceId: getFromRuleKey(safeKey, 'device'),
              swid: getFromRuleKey(safeKey, 'swid'),
              fuboUserId: getFromRuleKey(safeKey, 'fuboUser'),
              viewerId: getFromRuleKey(safeKey, 'viewer'),
              podPositions: Object.entries(mappedPositions).map(([pod, slots]) => ({
                title: (
                  <>
                    <Tag color="volcano">Pod</Tag>
                    {pod}
                  </>
                ),
                key: `${key}-${pod}`,
                children: slots.map(({ number, 'ad-id': adId }, slotIndex) => ({
                  title: (
                    <>
                      <div>
                        <Tag color="magenta">Slot</Tag>
                        <span style={{ userSelect: 'text' }}>{number}</span>
                        <Tag color="lime" style={{ marginLeft: '8px' }}>
                          Ad
                        </Tag>
                        <span style={{ userSelect: 'text' }}>{adId}</span>
                      </div>
                    </>
                  ),
                  key: `${key}-${pod}-${adId}-${slotIndex}`,
                })),
              })),
              mappedPositions,
              mappedPositionsString: JSON.stringify(mappedPositions),
              app,
              expireAt,
              expireAtReadableFormat: moment.unix(expireAt).format('MM/DD/YYYY HH:mm:ss'),
              editorEmail,
            };
          }
        );
        combinedRules.push(...rules);
      }

      if (pauseAdsOverridesResponse?.data) {
        const response = pauseAdsOverridesResponse;
        const fetchedRules = Array.isArray(response.data) ? response.data : [response.data];
        const rules = fetchedRules.map(
          ({ key, 'ad-id': adId, 'expire-at': expireAt, 'editor-email': editorEmail }, ruleIndex) => {
            const app = extractAppFromRule(key);
            return {
              key: key + ruleIndex,
              adType: PAUSE_AD_TYPE,
              userId: getFromRuleKey(key, 'account'),
              profileId: getFromRuleKey(key, 'profile'),
              deviceId: getFromRuleKey(key, 'device'),
              podPositions: (
                <div>
                  <Tag color="lime">Ad</Tag> <span style={{ userSelect: 'text' }}>{adId}</span>
                </div>
              ),
              pauseAdId: adId,
              app,
              expireAt,
              expireAtReadableFormat: moment.unix(expireAt).format('MM/DD/YYYY HH:mm:ss'),
              editorEmail,
            };
          }
        );
        combinedRules.push(...rules);
      }

      yield put({ type: 'updateExistingRules', payload: combinedRules });
    },
  },
};
